import { currentUserId, supabase } from '../auth/supabase'

/**
 * Push subscriptions, which are the one thing here that is *not* synced.
 *
 * A subscription belongs to one browser, not to the account: syncing it would
 * hand the phone's endpoint to the PC, which cannot use it and would push it
 * back with its own timestamp. So this module talks to `push_subscriptions`
 * directly, keyed by `(user_id, endpoint)`, and never appears in
 * `src/sync/schema.ts`.
 *
 * The reminder *hour* is the opposite: it is household data and travels
 * through the sync engine like everything else. Nothing here writes it —
 * `saveSettings` in the store does.
 */

// The endpoint this browser last wrote to `push_subscriptions`. Kept out of the
// synced store on purpose: a subscription belongs to one browser, and syncing it
// would hand the phone's endpoint to the PC. Versioned like every other key here
// so it can be abandoned rather than migrated.
const PUSH_ENDPOINT_KEY = 'maison_zen_push_endpoint_v1'

function rememberEndpoint(endpoint: string) {
  try {
    localStorage.setItem(PUSH_ENDPOINT_KEY, endpoint)
  } catch {
    // Private mode or a full quota: the reconciliation then re-adopts the
    // subscription on the next start, which is idempotent.
  }
}

function rememberedEndpoint() {
  try {
    return localStorage.getItem(PUSH_ENDPOINT_KEY)
  } catch {
    return null
  }
}

// Called when the user turns reminders off. Without this the reconciliation
// below would treat the missing subscription as damage and re-create it, so
// switching reminders off would undo itself on the next start. It matters more
// once docs/web-push.md task 5 makes disabling actually unsubscribe.
export function forgetPushEndpoint() {
  try {
    localStorage.removeItem(PUSH_ENDPOINT_KEY)
  } catch {
    // Nothing to forget if storage is unavailable.
  }
}

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  return Uint8Array.from([...raw].map((char) => char.charCodeAt(0)))
}

export async function enablePush() {
  if (!supabase) throw new Error('Supabase n’est pas encore configuré.')
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    throw new Error('Les notifications Push ne sont pas disponibles sur ce navigateur.')
  }

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    throw new Error('La permission de notification a été refusée.')
  }

  const vapid = import.meta.env.VITE_VAPID_PUBLIC_KEY
  if (!vapid) throw new Error('La clé VAPID publique manque.')

  const registration = await navigator.serviceWorker.ready
  let subscription = await registration.pushManager.getSubscription()

  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapid),
    })
  }

  const userId = await currentUserId()
  if (!userId) throw new Error('Connecte-toi d’abord pour recevoir les rappels.')

  await writeSubscription(userId, subscription)
}

async function writeSubscription(userId: string, subscription: PushSubscription) {
  if (!supabase) return

  const json = subscription.toJSON()
  const { error } = await supabase.from('push_subscriptions').upsert(
    {
      user_id: userId,
      endpoint: json.endpoint,
      p256dh: json.keys?.p256dh,
      auth: json.keys?.auth,
      user_agent: navigator.userAgent,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'user_id,endpoint' },
  )

  if (error) throw error
  if (json.endpoint) rememberEndpoint(json.endpoint)
}

// Moves an existing row onto a new endpoint. The function authenticates by
// possession of `oldEndpoint` — no session is involved, which is what lets the
// service worker call the same endpoint with no page open. Returns false when
// there was no row to move; the caller then falls back to a normal write.
async function rotateSubscription(oldEndpoint: string, subscription: PushSubscription) {
  if (!__ROTATE_SUBSCRIPTION_URL__) return false

  const json = subscription.toJSON()
  if (!json.endpoint) return false

  const response = await fetch(__ROTATE_SUBSCRIPTION_URL__, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      oldEndpoint,
      endpoint: json.endpoint,
      p256dh: json.keys?.p256dh ?? '',
      auth: json.keys?.auth ?? '',
    }),
  })

  if (response.status === 404) return false
  if (!response.ok) throw new Error(`rotate-subscription: ${response.status}`)

  rememberEndpoint(json.endpoint)
  return true
}

// The safety net under `pushsubscriptionchange`. That event is not fired at all
// on iOS, and is fired with a device offline often enough elsewhere, so the
// handler alone is not a fix on the platform this app actually runs on. Compare
// the live subscription against the endpoint last written and repair the
// difference. Runs at startup, never blocks the UI, and never prompts: a
// permission the user has not granted is left exactly as it is.
export async function reconcilePushSubscription() {
  if (!supabase || !navigator.onLine) return
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return
  if (!('Notification' in window) || Notification.permission !== 'granted') return

  const registration = await navigator.serviceWorker.ready
  let subscription = await registration.pushManager.getSubscription()
  const known = rememberedEndpoint()

  if (!subscription) {
    // The browser dropped the subscription outright. Re-subscribe only if this
    // browser had one before: with no remembered endpoint there is nothing to
    // repair, and subscribing would create a row the user never asked for.
    if (!known) return

    const vapid = import.meta.env.VITE_VAPID_PUBLIC_KEY
    if (!vapid) return
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapid),
    })
  }

  if (subscription.endpoint === known) return

  // Rotating in place keeps whatever the row already carries — today `user_id`,
  // tomorrow a household and a member. Re-deriving it from this device would
  // silently re-scope the subscription.
  if (known && (await rotateSubscription(known, subscription))) return

  // No row to rotate from. Write it directly, but only under a session that
  // already exists: startup must never create one.
  const userId = await currentUserId()
  if (!userId) return

  await writeSubscription(userId, subscription)
}
